import React,{useEffect,useMemo,useState}from"react";
import{Box,Paper,Typography,Button,TextField,Table,TableBody,TableCell,TableContainer,TableHead,TableRow,TablePagination,useMediaQuery}from"@mui/material";
import AddRoundedIcon from"@mui/icons-material/AddRounded";
import EditRoundedIcon from"@mui/icons-material/EditRounded";
import{useNavigate}from"react-router-dom";
import Sidebar from"./AdminPanel/Sidebar";
import TopBar from"./AdminPanel/TopBar";
import{useDashboardTheme}from"../../context/DashboardThemeContext";
import{useAlert}from"../../context/AlertContext";
import{useAuth}from"../../context/AuthContext";

function formatMoney(value){
  const number=Number(value||0);
  return number.toLocaleString("es-EC",{minimumFractionDigits:2,maximumFractionDigits:2});
}

function formatPercent(value){
  if(Number(value)===999)return"No objeto";
  return`${Number(value||0)}%`;
}

function getIvaValue(product){
  if(product.iva_valor!==undefined&&product.iva_valor!==null)return Number(product.iva_valor||0);
  if(Number(product.iva_porcentaje)===999)return 0;
  return Number(product.pvp1||0)*(Number(product.iva_porcentaje||0)/100);
}

function getFinalPrice(product){
  return Number(product.pvp1||0)+getIvaValue(product);
}

function userCan(user,code){
  if(!user)return false;
  if(user.role==="super_admin"||user.role==="admin")return true;
  return(user.permissions||[]).includes(code);
}

export default function AdminProducts(){
  const[sidebarOpen,setSidebarOpen]=useState(true);
  const{dashboardTheme}=useDashboardTheme();
  const{showError}=useAlert();
  const{user}=useAuth();
  const navigate=useNavigate();
  const isMobile=useMediaQuery("(max-width:900px)");
  const[products,setProducts]=useState([]);
  const[search,setSearch]=useState("");
  const[loading,setLoading]=useState(false);
  const[page,setPage]=useState(0);
  const rowsPerPage=isMobile?10:30;
  const token=localStorage.getItem("accessToken");
  const canView=userCan(user,"products.view");
  const canCreate=userCan(user,"products.create");
  const canEdit=userCan(user,"products.edit");
  const canDelete=userCan(user,"products.delete");
  const showActions=canEdit||canDelete;
  const canManageCatalog=canCreate||canEdit||canDelete;
  const tableColumns=showActions?7:6;

  const loadProducts=async()=>{
    if(!canView)return;
    try{
      setLoading(true);
      const params=new URLSearchParams();
      if(search.trim())params.append("q",search.trim());
      const response=await fetch(`/api/products/products/?${params.toString()}`,{
        headers:{Authorization:`Bearer ${token}`}
      });
      const data=await response.json();
      if(!response.ok){
        showError(data.error||data.detail||"No se pudieron cargar los productos");
        return;
      }
      setProducts(data.results||[]);
    }catch{
      showError("Error de conexión");
    }finally{
      setLoading(false);
    }
  };

  useEffect(()=>{
    const delay=setTimeout(()=>{
      setPage(0);
      loadProducts();
    },350);
    return()=>clearTimeout(delay);
  },[search,canView]);

  useEffect(()=>{
    setPage(0);
  },[rowsPerPage]);

  const visibleProducts=useMemo(()=>{
    const start=page*rowsPerPage;
    return products.slice(start,start+rowsPerPage);
  },[products,page,rowsPerPage]);

  const handleChangePage=(_event,newPage)=>{
    setPage(newPage);
  };

  const outlinedButtonSx={
    minHeight:40,
    px:2.4,
    borderRadius:"12px",
    textTransform:"none",
    fontWeight:700,
    color:dashboardTheme.button_color,
    borderColor:dashboardTheme.button_color,
    boxShadow:"none",
    "&:hover":{
      borderColor:dashboardTheme.button_color,
      bgcolor:`${dashboardTheme.button_color}12`,
      boxShadow:"none"
    }
  };

  const smallButtonSx={
    minHeight:34,
    px:1.6,
    borderRadius:"10px",
    textTransform:"none",
    fontWeight:700,
    color:dashboardTheme.button_color,
    borderColor:dashboardTheme.button_color,
    boxShadow:"none",
    "&:hover":{
      borderColor:dashboardTheme.button_color,
      bgcolor:`${dashboardTheme.button_color}12`,
      boxShadow:"none"
    }
  };

  return(
    <Box sx={{minHeight:"100vh",bgcolor:dashboardTheme.dashboard_background,display:"flex",color:dashboardTheme.text_color,overflowX:"hidden"}}>
      <Sidebar open={sidebarOpen} onClose={()=>setSidebarOpen(false)}/>
      <Box sx={{flex:1,minWidth:0,minHeight:"100vh",display:"flex",flexDirection:"column"}}>
        <TopBar sidebarOpen={sidebarOpen} onToggleSidebar={()=>setSidebarOpen(prev=>!prev)}/>
        <Box component="main" sx={{flex:1,width:"100%",p:{xs:1.5,sm:2,md:3}}}>
          <Box sx={{width:{xs:"100%",lg:"95%"},mx:"auto",mt:{xs:1,md:1.5}}}>
            <Box sx={{display:"flex",alignItems:{xs:"stretch",sm:"center"},justifyContent:"space-between",flexDirection:{xs:"column",sm:"row"},gap:1.5,mb:{xs:1.5,md:2}}}>
              <Typography sx={{fontSize:{xs:24,sm:26,md:30},fontWeight:600,color:dashboardTheme.primary_color,lineHeight:1.2}}>Productos</Typography>
              {(canManageCatalog||canCreate)&&(
                <Box sx={{display:"flex",flexDirection:{xs:"column",sm:"row"},gap:1}}>
                  {canManageCatalog&&(
                    <Button variant="outlined" onClick={()=>navigate("/admin-panel/productos/categorias")} sx={outlinedButtonSx}>
                      Administrar Categorías/Marcas
                    </Button>
                  )}
                  {canCreate&&(
                    <Button variant="contained" startIcon={<AddRoundedIcon/>} onClick={()=>navigate("/admin-panel/productos/nuevo")} sx={dashboardTheme.buttonStyles.contained}>
                      Añadir nuevo producto
                    </Button>
                  )}
                </Box>
              )}
            </Box>
            <Paper elevation={0} sx={dashboardTheme.tableStyles.wrapper}>
              <Box sx={dashboardTheme.tableStyles.toolbar}>
                <TextField fullWidth size="small" label="Buscar por nombre, código, categoría o marca" value={search} onChange={e=>setSearch(e.target.value)} sx={{maxWidth:{xs:"100%",md:420},...dashboardTheme.inputStyles}}/>
              </Box>
              <Box sx={dashboardTheme.tableStyles.tableArea}>
                <TableContainer sx={dashboardTheme.tableStyles.container}>
                  <Table size="small" sx={dashboardTheme.tableStyles.table}>
                    <TableHead>
                      <TableRow sx={dashboardTheme.tableStyles.headRow}>
                        <TableCell sx={{...dashboardTheme.tableStyles.headCell,minWidth:260}}>Producto</TableCell>
                        <TableCell sx={dashboardTheme.tableStyles.headCell}>Código</TableCell>
                        <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>PVP</TableCell>
                        <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>IVA</TableCell>
                        <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Precio final</TableCell>
                        <TableCell sx={dashboardTheme.tableStyles.headCell}>Estado</TableCell>
                        {showActions&&(
                          <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Acciones</TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visibleProducts.map(product=>{
                        const productId=product.id||product.pk;
                        return(
                          <TableRow key={productId} hover sx={{...dashboardTheme.tableStyles.bodyRow,cursor:canEdit?"pointer":"default"}} onDoubleClick={()=>{
                            if(!canEdit)return;
                            if(productId)navigate(`/admin-panel/productos/${productId}`);
                          }}>
                            <TableCell>
                              <Box sx={{display:"flex",alignItems:"center",gap:1.2}}>
                                <Typography sx={{fontSize:14,fontWeight:600,color:dashboardTheme.text_color,lineHeight:1.25}}>
                                  {product.name||"-"}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={{whiteSpace:"nowrap",color:dashboardTheme.text_color}}>
                              {product.code||"-"}
                            </TableCell>
                            <TableCell align="right" sx={{whiteSpace:"nowrap"}}>
                              ${formatMoney(product.pvp1)}
                            </TableCell>
                            <TableCell align="right" sx={{whiteSpace:"nowrap"}}>
                              <Box sx={{display:"flex",flexDirection:"column",alignItems:"flex-end"}}>
                                <Typography sx={{fontSize:13,fontWeight:600,color:dashboardTheme.text_color,lineHeight:1.25}}>
                                  {formatPercent(product.iva_porcentaje)}
                                </Typography>
                                <Typography sx={{fontSize:12.5,fontWeight:500,color:dashboardTheme.theme_mode==="dark"?"rgba(255,255,255,0.62)":"#4b5563",lineHeight:1.25}}>
                                  ${formatMoney(getIvaValue(product))}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right" sx={{whiteSpace:"nowrap"}}>
                              <Typography sx={{fontSize:14,fontWeight:700,color:dashboardTheme.primary_color,lineHeight:1.25}}>
                                ${formatMoney(getFinalPrice(product))}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{whiteSpace:"nowrap"}}>
                              {product.estado==="activo"?"Activo":"Inactivo"}
                            </TableCell>
                            {showActions&&(
                              <TableCell align="right" sx={{whiteSpace:"nowrap"}}>
                                {canEdit&&(
                                  <Button variant="outlined" size="small" startIcon={<EditRoundedIcon/>} onClick={event=>{
                                    event.stopPropagation();
                                    if(productId){
                                      navigate(`/admin-panel/productos/${productId}`);
                                    }else{
                                      showError("No se pudo abrir el producto porque no tiene ID.");
                                    }
                                  }} sx={smallButtonSx}>
                                    Editar
                                  </Button>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                      {!loading&&visibleProducts.length===0&&canView&&(
                        <TableRow>
                          <TableCell colSpan={tableColumns} align="center" sx={dashboardTheme.tableStyles.emptyCell}>
                            No hay productos registrados
                          </TableCell>
                        </TableRow>
                      )}
                      {loading&&(
                        <TableRow>
                          <TableCell colSpan={tableColumns} align="center" sx={dashboardTheme.tableStyles.emptyCell}>
                            Cargando productos...
                          </TableCell>
                        </TableRow>
                      )}
                      {!canView&&(
                        <TableRow>
                          <TableCell colSpan={tableColumns} align="center" sx={dashboardTheme.tableStyles.emptyCell}>
                            No tienes permisos para ver productos
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              <TablePagination component="div" count={products.length} page={page} onPageChange={handleChangePage} rowsPerPage={rowsPerPage} rowsPerPageOptions={[]} labelDisplayedRows={({from,to,count})=>`${from}-${to} de ${count}`} sx={dashboardTheme.tableStyles.pagination}/>
            </Paper>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}